import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TTSButton } from './TTSButton';
import { WordItem } from '../types';
import { ALL_UNIQUE_WORDS } from '../data/gameData';
import { PlayerSprite } from './Sprites';
import { CelestialLevelArtwork } from './CelestialLevelArtwork';
import { playSound, playBGM } from '../audio';
import { getWordIcon } from '../utils/emojis';
import { useKabyleTTS } from '../hooks/useKabyleTTS';
import { MusicToggleButton } from './MusicToggleButton';
import { formatPromptWithGrammar, stripGrammarParentheses } from '../utils/kabyleGrammar';
import {
  formatCelestialTime,
  getCelestialLeaderboard,
  getPlayerCelestialHistory,
  getPlayerBestTime,
  getPlayerPreviousAttempt,
  saveCelestialRunRecord,
  CelestialRecord,
  SaveRecordResult
} from '../utils/celestialLeaderboard';

interface CelestialMoonChallengeProps {
  currentSkin: string;
  playerName?: string;
  onBack: () => void;
  onAddScore?: (xp: number) => void;
  onComplete?: () => void;
  onAdminSwitchLevel?: (lvlIdx: number) => void;
}

interface QuestionData {
  targetWord: WordItem;
  direction: 'FR_TO_KAB' | 'KAB_TO_FR';
  options: WordItem[];
  questionNumber: number;
}

export interface CelestialNode {
  index: number;
  x: number;
  y: number;
  altitudeTag?: string;
  color?: string;
  altKm?: number;
}

// Long SVG Map dimensions (aligned with standard level height)
const TOTAL_QUESTIONS = 50;
const MAP_WIDTH = 400;
const MAP_HEIGHT = 4400;
const START_Y = 4320;
const SUMMIT_Y = 200;
const DISTANCE_MOON_KM = 384400;

// 51 Star Nodes forming real constellations
export const CELESTIAL_PATH_NODES: CelestialNode[] = Array.from({ length: TOTAL_QUESTIONS + 1 }).map((_, i) => {
  if (i === 0) {
    return {
      index: 0,
      x: 200,
      y: START_Y,
      altitudeTag: 'Sommet du Djurdjura (0 km)',
      color: '#fef08a',
      altKm: 0,
    };
  }

  if (i === TOTAL_QUESTIONS) {
    return {
      index: TOTAL_QUESTIONS,
      x: 200,
      y: SUMMIT_Y,
      altitudeTag: `Sanctuaire d'Ayyur (${DISTANCE_MOON_KM.toLocaleString('fr-FR')} km)`,
      color: '#fef08a',
      altKm: DISTANCE_MOON_KM,
    };
  }

  const y = Math.round(START_Y - (i / TOTAL_QUESTIONS) * (START_Y - SUMMIT_Y));
  const altKm = Math.round((i / TOTAL_QUESTIONS) * DISTANCE_MOON_KM);

  let x = 200;
  let color = '#38bdf8';
  let altitudeTag: string | undefined;

  if (i <= 10) { 
    // Constellation 1: The Jackal (Uccen) - Orange
    color = '#fb923c'; 
    const offsets = [0, -40, 30, 70, 20, -50, -90, -20, 50, 80, 0];
    x = 200 + offsets[i];
  } else if (i <= 20) { 
    // Constellation 2: The Eagle (Ijduden) - Cyan
    color = '#22d3ee'; 
    const offsets = [0, 70, 110, 50, -10, -80, -100, -30, 40, 80, 0];
    x = 200 + offsets[i - 10];
  } else if (i <= 30) { 
    // Constellation 3: The Olive Tree (Azemmur) - Emerald
    color = '#34d399'; 
    const offsets = [0, -50, -10, 60, 100, 70, 0, -70, -30, 40, 0];
    x = 200 + offsets[i - 20];
  } else if (i <= 40) { 
    // Constellation 4: The Ram (Ameksa) - Purple
    color = '#c084fc'; 
    const offsets = [0, 40, -30, -90, -20, 80, 110, 50, -40, 30, 0];
    x = 200 + offsets[i - 30];
  } else { 
    // Constellation 5: The Yaz (ⵣ) - Golden
    color = '#facc15'; 
    const offsets = [0, -70, -20, 50, 80, 10, -50, -10, 40, 0];
    x = 200 + offsets[i - 40];
  }

  // Cap x to stay nicely within view
  x = Math.max(50, Math.min(350, x));

  return {
    index: i,
    x,
    y,
    color,
    altitudeTag,
    altKm,
  };
});

// Lightweight sub-component to isolate live chronometer updates and prevent re-rendering the heavy 4400px SVG stage
const CelestialLiveChrono: React.FC<{
  startTime: number;
  isRunning: boolean;
  finalSeconds?: number | null;
}> = React.memo(({ startTime, isRunning, finalSeconds }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 200);
    return () => clearInterval(interval);
  }, [isRunning]);

  const elapsed = finalSeconds != null ? finalSeconds : Math.max(0, (now - startTime) / 1000);

  return (
    <span className="font-mono font-black text-sm sm:text-base text-cyan-300 tracking-wider drop-shadow-[0_0_4px_rgba(34,211,238,0.9)]">
      {formatCelestialTime(elapsed, true)}
    </span>
  );
});

export const CelestialMoonChallenge: React.FC<CelestialMoonChallengeProps> = ({
  currentSkin,
  playerName = 'Voyageur',
  onBack,
  onAddScore,
  onComplete,
  onAdminSwitchLevel,
}) => {
  const INITIAL_LIVES = 5;

  // State
  const [lives, setLives] = useState<number>(INITIAL_LIVES);
  const [currentQIndex, setCurrentQIndex] = useState<number>(0);
  const [failedOptionKabs, setFailedOptionKabs] = useState<string[]>([]);
  const [combo, setCombo] = useState<number>(0);
  const [maxCombo, setMaxCombo] = useState<number>(0);
  const [feedback, setFeedback] = useState<'IDLE' | 'CORRECT' | 'WRONG'>('IDLE');
  const [selectedOption, setSelectedOption] = useState<WordItem | null>(null);
  const [isGameOver, setIsGameOver] = useState<boolean>(false);
  const [isVictory, setIsVictory] = useState<boolean>(false);

  // High precision speedrun Chronometer start and final freeze
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  const [finalElapsedSeconds, setFinalElapsedSeconds] = useState<number | null>(null);

  // Leaderboard & Record tracking state
  const [runResult, setRunResult] = useState<SaveRecordResult | null>(null);
  const [leaderboardTab, setLeaderboardTab] = useState<'GLOBAL' | 'MY_HISTORY'>('GLOBAL');
  const [showLeaderboardModal, setShowLeaderboardModal] = useState<boolean>(false);

  // Personal Best before this run for HUD comparison
  const [initialPB, setInitialPB] = useState<CelestialRecord | null>(() => getPlayerBestTime(playerName));

  // Animations
  const [isWalking, setIsWalking] = useState<boolean>(false);
  const { playTTS } = useKabyleTTS();
  const [isJumping, setIsJumping] = useState<boolean>(false);
  const [showCoinPop, setShowCoinPop] = useState<boolean>(false);
  const [popMessage, setPopMessage] = useState<string>('Ayyuz !');

  // Pool of 50 questions
  const [questionsPool, setQuestionsPool] = useState<QuestionData[]>([]);

  // Stage viewport measurement
  const viewportRef = useRef<HTMLDivElement>(null);
  const gameContainerRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(300);
  const [containerWidth, setContainerWidth] = useState<number>(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 480;
  });

  // Measure top screen viewport and width
  useEffect(() => {
    let rafId: number | null = null;
    let lastW = 0;
    let lastH = 0;

    const scheduleUpdate = (rawW?: number, rawH?: number) => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        let w = rawW ?? (gameContainerRef.current?.clientWidth || viewportRef.current?.clientWidth || window.innerWidth);
        let h = rawH ?? (viewportRef.current?.clientHeight || 300);

        w = Math.round(w);
        h = Math.round(h);

        if (w > 0 && Math.abs(w - lastW) >= 4) {
          lastW = w;
          setContainerWidth(w);
        }
        if (h > 0 && Math.abs(h - lastH) >= 4) {
          lastH = h;
          setViewportHeight(h);
        }
      });
    };

    scheduleUpdate();

    let ro: ResizeObserver | null = null;
    const el = gameContainerRef.current || viewportRef.current;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const w = entry.contentRect.width || entry.contentBoxSize?.[0]?.inlineSize;
          const h = entry.contentRect.height || entry.contentBoxSize?.[0]?.blockSize;
          if ((w && w > 0) || (h && h > 0)) {
            scheduleUpdate(w, h);
          }
        }
      });
      ro.observe(el);
    }

    window.addEventListener('resize', () => scheduleUpdate(), { passive: true });
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', () => scheduleUpdate());
    };
  }, []);

  const viewBoxWidth = Math.max(400, containerWidth);
  const viewBoxLeft = Math.round(200 - viewBoxWidth / 2);

  // Generate 50 questions
  const createQuestionPool = useCallback((): QuestionData[] => {
    const shuffled = [...ALL_UNIQUE_WORDS].sort(() => Math.random() - 0.5);
    const selectedWords: WordItem[] = [];
    let idx = 0;
    while (selectedWords.length < TOTAL_QUESTIONS) {
      selectedWords.push(shuffled[idx % shuffled.length]);
      idx++;
    }

    return selectedWords.map((word, qIdx) => {
      const direction: 'FR_TO_KAB' | 'KAB_TO_FR' = qIdx % 3 === 0 ? 'KAB_TO_FR' : 'FR_TO_KAB';
      const otherWords = ALL_UNIQUE_WORDS.filter(
        w => w.kab !== word.kab && w.fr !== word.fr
      );
      const shuffledOthers = [...otherWords].sort(() => Math.random() - 0.5);
      const distractors: WordItem[] = [];
      const usedTranslations = new Set<string>([word.kab, word.fr]);

      for (const candidate of shuffledOthers) {
        if (distractors.length >= 3) break;
        if (!usedTranslations.has(candidate.kab) && !usedTranslations.has(candidate.fr)) {
          distractors.push(candidate);
          usedTranslations.add(candidate.kab);
          usedTranslations.add(candidate.fr);
        }
      }

      const options = [word, ...distractors].sort(() => Math.random() - 0.5);

      return {
        targetWord: word,
        direction,
        options,
        questionNumber: qIdx + 1,
      };
    });
  }, []);

  // Initialize pool on mount
  useEffect(() => {
    setQuestionsPool(createQuestionPool());
    setStartTime(Date.now());
    setInitialPB(getPlayerBestTime(playerName));
    try {
      playBGM('boss');
    } catch {}
  }, [createQuestionPool, playerName]);

  // Current active question
  const currentQuestion = useMemo(() => {
    return questionsPool[currentQIndex] || null;
  }, [questionsPool, currentQIndex]);

  // Reset failed options when changing question
  useEffect(() => {
    setFailedOptionKabs([]);
  }, [currentQIndex]);

  // Current active star node
  const activeNode = CELESTIAL_PATH_NODES[Math.min(currentQIndex, TOTAL_QUESTIONS)];

  // Camera offset calculation matching game level camera tracking
  const cameraOffsetY = useMemo(() => {
    const rawOffset = -(activeNode.y - viewportHeight * 0.58);
    const maxOffset = 0;
    const minOffset = -(MAP_HEIGHT - viewportHeight);
    return Math.max(minOffset, Math.min(maxOffset, rawOffset));
  }, [activeNode.y, viewportHeight]);

  // Handle victory completion and saving run record
  const triggerVictory = useCallback((finalSeconds: number, finalLives: number, finalMaxCombo: number) => {
    setIsVictory(true);
    setFinalElapsedSeconds(finalSeconds);
    playSound('victory');

    if (onAddScore) onAddScore(2500);

    const result = saveCelestialRunRecord(
      playerName,
      finalSeconds,
      true,
      TOTAL_QUESTIONS,
      finalLives,
      finalMaxCombo
    );

    setRunResult(result);
    setInitialPB(getPlayerBestTime(playerName));

    if (onComplete) onComplete();
  }, [playerName, onComplete, onAddScore]);

  // Handle game over completion
  const triggerGameOver = useCallback((finalSeconds: number, starsReached: number, finalMaxCombo: number) => {
    setIsGameOver(true);
    setFinalElapsedSeconds(finalSeconds);
    playSound('wrong');

    const result = saveCelestialRunRecord(
      playerName,
      finalSeconds,
      false,
      starsReached,
      0,
      finalMaxCombo
    );
    setRunResult(result);
  }, [playerName]);

  // Handle answer click
  const handleAnswer = (option: WordItem) => {
    if (feedback !== 'IDLE' || isGameOver || isVictory || !currentQuestion) return;
    if (failedOptionKabs.includes(option.kab)) return;

    setSelectedOption(option);
    const isCorrect = option.kab === currentQuestion.targetWord.kab;

    if (isCorrect) {
      setFeedback('CORRECT');
      playSound('correct');
      setIsWalking(true);
      setIsJumping(true);

      const newCombo = combo + 1;
      setCombo(newCombo);
      const currentMaxCombo = Math.max(maxCombo, newCombo);
      setMaxCombo(currentMaxCombo);

      if (onAddScore) onAddScore(50);

      if (newCombo >= 10) {
        setPopMessage('⚡ Foudre !');
      } else if (newCombo >= 5) {
        setPopMessage('🔥 Rapide !');
      } else {
        setPopMessage('Ayyuz !');
      }
      setShowCoinPop(true);

      setTimeout(() => {
        setIsJumping(false);
        setIsWalking(false);
        setShowCoinPop(false);
        setFeedback('IDLE');
        setSelectedOption(null);
        setFailedOptionKabs([]);

        if (currentQIndex + 1 >= TOTAL_QUESTIONS) {
          const finalTime = (Date.now() - startTime) / 1000;
          triggerVictory(finalTime, lives, currentMaxCombo);
        } else {
          setCurrentQIndex(prev => prev + 1);
        }
      }, 1200);
    } else {
      setFeedback('WRONG');
      playSound('wrong');
      setCombo(0);

      const newLives = lives - 1;
      setLives(newLives);
      setFailedOptionKabs(prev => (prev.includes(option.kab) ? prev : [...prev, option.kab]));

      setTimeout(() => {
        setFeedback('IDLE');
        setSelectedOption(null);

        if (newLives <= 0) {
          const finalTime = (Date.now() - startTime) / 1000;
          triggerGameOver(finalTime, currentQIndex + 1, maxCombo);
        }
        // If still alive, stay on the current question. The player must retry until finding the right answer.
      }, 1800);
    }
  };

  // Restart challenge with fresh chronometer
  const handleRestart = () => {
    playSound('click');
    setLives(INITIAL_LIVES);
    setCurrentQIndex(0);
    setFailedOptionKabs([]);
    setCombo(0);
    setMaxCombo(0);
    setFeedback('IDLE');
    setSelectedOption(null);
    setIsGameOver(false);
    setIsVictory(false);
    setRunResult(null);
    setShowLeaderboardModal(false);
    setStartTime(Date.now());
    setFinalElapsedSeconds(null);
    setQuestionsPool(createQuestionPool());
    setInitialPB(getPlayerBestTime(playerName));
  };

  const progressPercent = Math.round((currentQIndex / TOTAL_QUESTIONS) * 100);
  const moonBrightness = Math.min(100, Math.round(20 + (currentQIndex / TOTAL_QUESTIONS) * 80));

  // Current leaderboard entries
  const globalLeaderboard = useMemo(() => getCelestialLeaderboard(), [isVictory, isGameOver]);
  const playerHistory = useMemo(() => getPlayerCelestialHistory(playerName), [isVictory, isGameOver, playerName]);

  return (
    <div className="w-full h-[100dvh] bg-black flex justify-center items-center overflow-hidden select-none">
      <div className="w-full h-full bg-black flex flex-col font-mono text-slate-800 overflow-hidden relative shadow-2xl transition-all duration-300">
        {/* Flash de couleur unifié sur les 2 écrans sans ligne de séparation */}
        {feedback === 'CORRECT' && (
          <div className="absolute inset-0 bg-white/30 z-[80] pointer-events-none transition-opacity duration-150" />
        )}
        {feedback === 'WRONG' && (
          <div className="absolute inset-0 bg-red-900/60 z-[80] pointer-events-none flex items-center justify-center transition-opacity duration-150">
            <div className="absolute top-[27.5%] -translate-y-1/2 bg-black text-red-500 border-2 border-red-500 px-8 py-3 font-bold text-2xl uppercase tracking-widest shadow-[4px_4px_0_red] animate-in zoom-in-90 duration-150">
              RATÉ !
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* 1. ÉCRAN DU HAUT (55%) - EXACT FORMAT RETRO AVEC SCÈNE CONTINUE           */}
        {/* ========================================================================= */}
        <div
          ref={viewportRef}
          className="relative w-full h-[55%] bg-transparent flex flex-col overflow-visible z-10 -mb-[1px] shrink-0"
        >
          {/* HUD: Left Column (Hearts + Player Name in plain white text) */}
          <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 flex flex-col items-start gap-1 z-40 pointer-events-none">
            <div className="bg-slate-950/90 backdrop-blur-md border border-cyan-500/30 px-2 py-1.5 flex gap-1 rounded-full shadow-[0_0_10px_rgba(6,182,212,0.15)] pointer-events-auto">
              {Array.from({ length: INITIAL_LIVES }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 sm:w-3.5 sm:h-3.5 rounded-full ${
                    i < lives ? 'bg-red-500 shadow-[0_0_4px_rgba(239,68,68,0.8)]' : 'bg-slate-700/50'
                  } transition-colors`}
                />
              ))}
            </div>

            <div className="text-white text-xs sm:text-sm font-bold tracking-wide drop-shadow-[0_1px_3px_rgba(0,0,0,0.95)] px-0.5 select-none">
              {playerName || 'Voyageur'}
            </div>
          </div>

          {/* HUD: Right Column (SPEEDRUN CHRONOMETER + ALTITUDE GAUGE) */}
          <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 flex flex-col items-end gap-1.5 z-40 pointer-events-none">
            {/* Live Chronometer Pill */}
            <div className="bg-slate-950/90 backdrop-blur-md border border-cyan-400/60 px-2.5 py-1 text-white rounded-2xl flex flex-col items-end shadow-[0_0_15px_rgba(6,182,212,0.25)] pointer-events-auto">
              <div className="flex items-center gap-1.5">
                <span className="text-amber-400 text-xs animate-pulse">⏱️</span>
                <CelestialLiveChrono
                  startTime={startTime}
                  isRunning={!isGameOver && !isVictory}
                  finalSeconds={finalElapsedSeconds}
                />
              </div>

              {/* Personal Best indicator during run */}
              {initialPB ? (
                <div className="text-[8px] sm:text-[9px] font-mono text-amber-300/80 font-bold flex items-center gap-1">
                  <span>🏆 Record:</span>
                  <span>{initialPB.formattedTime}</span>
                </div>
              ) : (
                <div className="text-[8px] font-mono text-slate-400/80">
                  Course Céleste
                </div>
              )}

              {/* Combo Streak */}
              {combo > 1 && (
                <span className="text-amber-300 text-[9px] uppercase tracking-wider font-black drop-shadow-[0_0_3px_rgba(251,191,36,0.9)] animate-pulse">
                  ⚡ COMBO x{combo}
                </span>
              )}
            </div>

            {/* Moon Progress Altitude Gauge */}
            <div className="flex flex-col items-center bg-slate-950/85 backdrop-blur-md px-1.5 py-1.5 rounded-full border border-cyan-500/30 shadow-[0_0_10px_rgba(6,182,212,0.1)] pointer-events-auto">
              <div className="bg-amber-400/20 border border-amber-400/70 w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full mb-0.5 shadow-[0_0_6px_rgba(251,191,36,0.4)]">
                <span className="text-[10px] sm:text-xs">🌕</span>
              </div>
              <div className="text-[8px] font-mono text-cyan-200 mb-1 font-bold text-center leading-tight tracking-tight drop-shadow-md">
                {activeNode.altKm ? (activeNode.altKm / 1000).toFixed(0) : 0}k
                <span className="text-[7px] block text-cyan-400/70">km</span>
              </div>
              <div className="w-1.5 sm:w-2 h-12 sm:h-16 bg-slate-900/90 border border-slate-700/60 rounded-full relative overflow-hidden shadow-inner">
                <div
                  className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-cyan-500 via-amber-400 to-yellow-300 transition-all duration-500 ease-out shadow-[0_0_5px_rgba(251,191,36,0.8)]"
                  style={{ height: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          {/* HUD: Bottom Navigation Buttons */}
          <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1.5 z-50 w-full max-w-[calc(100%-1.25rem)] px-1">
            <button
              type="button"
              onClick={() => {
                playSound('click');
                onBack();
              }}
              className="text-[10px] sm:text-xs text-white hover:text-amber-200 uppercase tracking-wider font-black bg-red-900/90 hover:bg-red-800 px-3 py-1 rounded-full border border-red-500 transition-colors shadow-md cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1.5 active:scale-95"
            >
              <svg className="w-2.5 h-2.5 fill-white shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <polygon points="17,4 7,12 17,20" />
              </svg>
              <span className="text-white">Retour</span>
            </button>

            <button
              type="button"
              onClick={() => {
                playSound('click');
                setShowLeaderboardModal(true);
              }}
              className="text-[9px] sm:text-[10px] text-amber-300 hover:text-amber-200 uppercase tracking-wider font-bold bg-amber-950/80 px-3 py-1 rounded-full border border-amber-500/50 transition-colors shadow-[0_0_8px_rgba(245,158,11,0.25)] cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1 active:scale-95"
            >
              <span>🏆</span> <span>Classement</span>
            </button>

            <MusicToggleButton variant="compact" className="!p-1 sm:!p-1.5 !rounded-full !bg-black/85 !border-slate-600 shrink-0" />
          </div>

          {/* Long Scrolling Stage Canvas */}
          <div ref={gameContainerRef} className="w-full h-full relative flex-1 bg-transparent flex justify-center overflow-visible">
            <div
              className="relative w-full h-full transition-transform duration-500 ease-linear"
              style={{
                height: `${MAP_HEIGHT + 1200}px`,
                transform: `translateY(${cameraOffsetY}px)`,
                willChange: 'transform',
              }}
            >
              {/* Luminous Light Threads, Twinkling Stars & Constellations Artwork */}
              <CelestialLevelArtwork
                nodes={CELESTIAL_PATH_NODES}
                currentStep={currentQIndex}
                moonBrightness={moonBrightness}
                containerWidth={containerWidth}
              />

              {/* Altitude Tag Banners on Milestone Stars */}
              {CELESTIAL_PATH_NODES.map((node, idx) => {
                if (!node.altitudeTag) return null;
                const percentX = ((node.x - viewBoxLeft) / viewBoxWidth) * 100;
                return (
                  <div
                    key={`star-tag-${idx}`}
                    className="absolute z-20 pointer-events-none"
                    style={{
                      left: `${percentX}%`,
                      top: `${node.y - 20}px`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    <div className="text-[9px] text-slate-300 uppercase tracking-[0.2em] font-bold whitespace-nowrap drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] bg-black/60 px-2 py-0.5 rounded-full border border-cyan-500/30 backdrop-blur-xs">
                      {node.altitudeTag}
                    </div>
                  </div>
                );
              })}

              {/* Player Sprite Walking on the Glowing Starlight Thread */}
              <div
                className="absolute transition-all duration-500 ease-linear flex flex-col items-center pointer-events-none"
                style={{
                  left: `${((activeNode.x - viewBoxLeft) / viewBoxWidth) * 100}%`,
                  top: `${activeNode.y + 6}px`,
                  zIndex: 9999,
                  transform: 'translate(-50%, -100%) translateZ(10px)',
                }}
              >
                {showCoinPop && (
                  <div className="relative mb-1 flex items-center justify-center pointer-events-none">
                    <div className="text-xl sm:text-2xl font-black text-amber-300 drop-shadow-[0_0_8px_#fcd34d] animate-bounce z-10 font-cinzel">
                      {popMessage}
                    </div>
                    {/* Poussière d'étoiles scintillantes */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full">
                      {[...Array(8)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute top-1/2 left-1/2 w-1.5 h-1.5 bg-yellow-200 rounded-full shadow-[0_0_5px_#fef08a]"
                          style={{
                            '--rot': `${i * 45}deg`,
                            animation: 'particleExplode 0.8s ease-out forwards',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
                <PlayerSprite isWalking={isWalking} isJumping={isJumping} skin={currentSkin} />
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* 2. ÉCRAN DU BAS - QUIZ ARENA FLOTTANT SUR LE CIEL ÉTOILÉ                  */}
        {/* ========================================================================= */}
        <div className="relative w-full flex-1 min-h-0 bg-transparent flex flex-col overflow-visible z-20 border-t-0 shadow-none">
          <div className="w-full h-full pt-2.5 sm:pt-3 pb-3 sm:pb-3.5 px-3 sm:px-6 flex flex-col relative z-10 overflow-y-auto">
            {/* Active Question Arena */}
            {!isGameOver && !isVictory && currentQuestion && (
              <div className="w-full h-full flex flex-col gap-3 md:gap-4 pb-1 justify-between max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto">
                {/* Question Card - Identical styling to other levels without solid isolated background */}
                <div className="bg-black/92 sm:bg-black/95 backdrop-blur-md border border-slate-700/80 p-4 md:p-5 flex-shrink-0 flex flex-col justify-center overflow-y-auto min-h-[35%] relative rounded-lg md:rounded-xl shadow-2xl">
                  <div className="flex justify-between items-start mb-2 relative z-10">
                    <div className="text-[10px] sm:text-xs uppercase font-bold text-amber-400 flex items-center gap-1.5 drop-shadow-[0_0_2px_rgba(251,191,36,0.8)]">
                      <span>{getWordIcon(currentQuestion.targetWord)}</span>
                      <span>{currentQuestion.targetWord.type}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] sm:text-xs font-bold text-cyan-200 uppercase tracking-widest font-mono drop-shadow-md">
                        Étoile {currentQIndex + 1}/{TOTAL_QUESTIONS}
                      </span>
                      <div className="w-16 sm:w-20 h-1.5 sm:h-2 bg-black/50 rounded-full border border-slate-600 overflow-hidden shadow-inner">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-400 via-emerald-400 to-amber-400 transition-all duration-300 rounded-full"
                          style={{ width: `${((currentQIndex + 1) / TOTAL_QUESTIONS) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="text-white text-base sm:text-lg md:text-xl whitespace-normal break-words leading-snug relative z-10">
                    Traduis :{' '}
                    {currentQuestion.direction === 'FR_TO_KAB' && getWordIcon(currentQuestion.targetWord) && (
                      <span className="inline-block text-2xl sm:text-3xl mr-1.5 align-middle animate-bounce" aria-hidden="true">
                        {getWordIcon(currentQuestion.targetWord)}
                      </span>
                    )}
                    <span className="text-amber-400 font-black text-xl sm:text-2xl md:text-3xl tracking-wide drop-shadow-[0_0_4px_rgba(251,191,36,0.6)]">
                      « {formatPromptWithGrammar(currentQuestion.targetWord, currentQuestion.direction === 'FR_TO_KAB')} »
                    </span>
                    {currentQuestion.direction !== 'FR_TO_KAB' && (
                      <TTSButton text={currentQuestion.targetWord.kab} className="ml-2 bg-black/40 border border-transparent" />
                    )}
                  </div>
                </div>

                {/* 2x2 Option Buttons Grid - Same font size and padding as standard levels */}
                <div className="grid grid-cols-2 gap-3 md:gap-4 flex-1">
                  {currentQuestion.options.map((opt, i) => {
                    const optLabel = stripGrammarParentheses(currentQuestion.direction === 'FR_TO_KAB' ? opt.kab : opt.fr);
                    const optLen = optLabel.length;
                    const fontSizeClass =
                      optLen > 30
                        ? 'text-[11px] sm:text-xs md:text-sm leading-tight'
                        : optLen > 18
                        ? 'text-xs sm:text-sm md:text-base leading-snug'
                        : 'text-sm sm:text-base md:text-lg leading-tight';
                    const hasTTS = currentQuestion.direction === 'FR_TO_KAB';
                    const isSelected = selectedOption?.kab === opt.kab;
                    const isFailed = failedOptionKabs.includes(opt.kab);

                    let btnStyle =
                      'bg-black/90 hover:bg-black/98 backdrop-blur-md text-white border-2 border-slate-700/80 hover:border-cyan-400 hover:shadow-[0_0_12px_rgba(6,182,212,0.4)] shadow-lg';
                    
                    if (feedback === 'CORRECT' && isSelected) {
                      btnStyle = 'bg-cyan-900/90 backdrop-blur-md text-cyan-100 border-2 border-cyan-400 shadow-[0_0_15px_rgba(34,211,238,0.5)] scale-[1.02]';
                    } else if (feedback === 'WRONG' && isSelected) {
                      btnStyle = 'bg-red-900/90 backdrop-blur-md text-red-100 border-2 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.6)] scale-[0.98]';
                    } else if (isFailed) {
                      btnStyle = 'bg-red-950/70 text-red-400/60 border-2 border-red-900/70 line-through opacity-50 cursor-not-allowed pointer-events-none shadow-none';
                    }

                    return (
                      <div
                        role="button"
                        tabIndex={isFailed ? -1 : 0}
                        key={i}
                        onClick={() => !isFailed && handleAnswer(opt)}
                        onKeyDown={(e) => { if (e.key === "Enter" && feedback === "IDLE" && !isFailed) handleAnswer(opt); }}
                        className={`font-bold ${fontSizeClass} uppercase tracking-wider active:scale-[0.98] flex items-center justify-center text-center p-2.5 sm:p-3 md:p-4 whitespace-normal break-words transition-all rounded-lg md:rounded-xl cursor-pointer relative ${hasTTS ? 'pr-7 sm:pr-8' : ''} ${btnStyle}`}
                      >
                        <span className="max-w-full px-1 break-words">{optLabel}</span>
                        {hasTTS && (
                          <div className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2" onClick={e => e.stopPropagation()}>
                            <TTSButton text={opt.kab} className="hover:bg-slate-700 border-transparent" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Game Over Screen */}
            {isGameOver && (
              <div className="w-full h-full flex flex-col justify-between gap-2.5 sm:gap-3 md:gap-4 max-w-md md:max-w-lg lg:max-w-xl mx-auto py-1 sm:py-2">
                <div className="bg-black/90 border-2 border-red-500/80 p-3 sm:p-4 md:p-5 rounded-xl flex-1 overflow-hidden flex flex-col justify-center items-center text-center shadow-[0_0_15px_rgba(239,68,68,0.4)]">
                  <div className="text-3xl sm:text-4xl mb-1 sm:mb-2">🌑</div>
                  <h3 className="text-red-400 uppercase font-black text-base sm:text-lg md:text-xl mb-1">
                    Éclipse Céleste
                  </h3>
                  <div className="text-white text-xs sm:text-sm mb-2 leading-relaxed max-w-md">
                    Tu as perdu tes 5 vies à l'étoile <strong className="text-amber-400">{currentQIndex + 1}/{TOTAL_QUESTIONS}</strong> ({progressPercent}% d'ascension).
                  </div>

                  {/* Chrono time reached */}
                  <div className="bg-slate-900/90 border border-slate-700 px-3.5 py-1.5 sm:py-2 rounded-lg flex items-center gap-2">
                    <span className="text-amber-400 text-xs sm:text-sm">⏱️ Chrono arrêté à :</span>
                    <span className="text-cyan-300 font-mono font-black text-sm sm:text-base">
                      {formatCelestialTime(finalElapsedSeconds || 0, true)}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 sm:gap-3 w-full">
                  <button
                    type="button"
                    onClick={handleRestart}
                    className="flex-1 py-3 md:py-3.5 bg-gradient-to-r from-amber-700 to-yellow-600 hover:from-amber-600 hover:to-yellow-500 text-slate-950 font-black text-xs sm:text-sm uppercase tracking-wider border-2 border-amber-300 rounded-lg shadow-[2px_2px_0_#78350f] active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>⚡</span> <span>Recommencer</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowLeaderboardModal(true)}
                    className="py-3 md:py-3.5 px-3.5 md:px-5 bg-slate-900 hover:bg-slate-800 text-amber-300 font-bold text-xs sm:text-sm uppercase tracking-wider border-2 border-amber-500/60 rounded-lg shadow-[2px_2px_0_#b45309] active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1"
                  >
                    <span>🏆</span> <span>Classement</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      onBack();
                    }}
                    className="py-3 md:py-3.5 px-3 md:px-4 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs sm:text-sm uppercase tracking-wider border-2 border-slate-600 rounded-lg active:translate-y-0.5 transition-all cursor-pointer"
                  >
                    Carte
                  </button>
                </div>
              </div>
            )}

            {/* Victory Screen with Chrono Analysis & Leaderboard */}
            {isVictory && runResult && (
              <div className="w-full h-full flex flex-col justify-between gap-2.5 sm:gap-3 md:gap-4 max-w-lg md:max-w-2xl lg:max-w-3xl mx-auto py-1 sm:py-2">
                {/* Time & Comparison Card */}
                <div className="bg-slate-950/95 border-2 border-amber-400 p-3 sm:p-4 rounded-xl flex-1 overflow-y-auto flex flex-col justify-between items-center text-center shadow-[0_0_20px_rgba(251,191,36,0.4)]">
                  <div className="flex items-center justify-between w-full border-b border-amber-500/40 pb-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl">🌕</span>
                      <span className="text-amber-300 font-black text-xs sm:text-sm uppercase tracking-wider">
                        Ayyur est Libéré !
                      </span>
                    </div>

                    <div className="bg-amber-950/90 border border-amber-400/70 px-2 py-0.5 rounded-full text-[10px] text-amber-300 font-mono font-bold">
                      Rang #{runResult.rank}
                    </div>
                  </div>

                  {/* Main Chrono Stat Box */}
                  <div className="my-1.5 w-full bg-gradient-to-r from-cyan-950/80 via-slate-900/90 to-amber-950/80 border border-cyan-400/50 p-2 rounded-xl flex flex-col items-center gap-1">
                    <span className="text-[10px] uppercase font-bold text-cyan-200 tracking-wider">
                      ⏱️ Temps d'Ascension Chronométré
                    </span>
                    <span className="text-2xl sm:text-3xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 via-amber-300 to-cyan-300 drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]">
                      {runResult.record.formattedTime}
                    </span>

                    {/* Previous Run Comparison Badge */}
                    {runResult.diffWithPreviousAttempt !== null ? (
                      <div className="w-full mt-0.5 pt-1 border-t border-slate-700/80 flex items-center justify-center gap-1.5 text-[11px] font-bold">
                        {runResult.diffWithPreviousAttempt < 0 ? (
                          <div className="text-emerald-300 flex items-center gap-1 bg-emerald-950/70 border border-emerald-500/50 px-2 py-0.5 rounded-md">
                            <span>🚀</span>
                            <span>
                              -{Math.abs(runResult.diffWithPreviousAttempt).toFixed(1)}s plus rapide que la fois précédente !
                            </span>
                          </div>
                        ) : runResult.diffWithPreviousAttempt > 0 ? (
                          <div className="text-amber-300/90 flex items-center gap-1 bg-amber-950/70 border border-amber-600/50 px-2 py-0.5 rounded-md">
                            <span>⏱️</span>
                            <span>
                              +{runResult.diffWithPreviousAttempt.toFixed(1)}s vs tentative précédente ({formatCelestialTime(runResult.previousAttemptTime || 0)})
                            </span>
                          </div>
                        ) : (
                          <div className="text-cyan-300 bg-slate-800 px-2 py-0.5 rounded-md">
                            Même temps que la tentative précédente !
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-[10px] text-emerald-300 bg-emerald-950/60 border border-emerald-500/40 px-2 py-0.5 rounded-md">
                        ⭐ Premier temps de référence établi pour {playerName} !
                      </div>
                    )}

                    {/* New Personal Record Banner */}
                    {runResult.isNewPersonalBest && runResult.previousBestTime !== null && (
                      <div className="text-[10px] font-black uppercase tracking-wider text-amber-300 bg-amber-500/20 border border-amber-400 px-2 py-0.5 rounded-full animate-pulse mt-0.5">
                        🏆 Nouveau Record Personnel Battu !
                      </div>
                    )}
                  </div>

                  {/* Summary badges */}
                  <div className="flex items-center justify-center gap-2 text-[10px] text-slate-300 font-mono">
                    <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                      💖 {lives}/{INITIAL_LIVES} vies restantes
                    </span>
                    <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                      ⚡ Combo Max : x{maxCombo}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 w-full">
                  <button
                    type="button"
                    onClick={handleRestart}
                    className="flex-1 py-3 bg-gradient-to-r from-cyan-700 to-teal-600 hover:from-cyan-600 hover:to-teal-500 text-white font-bold text-xs uppercase tracking-wider border-2 border-cyan-400 rounded-lg shadow-[2px_2px_0_#0891b2] active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>🔄</span> <span>Nouveau Run</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setShowLeaderboardModal(true)}
                    className="flex-1 py-3 bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 text-slate-950 font-black text-xs uppercase tracking-wider border-2 border-amber-300 rounded-lg shadow-[2px_2px_0_#78350f] active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>🏆</span> <span>Classement</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      onBack();
                    }}
                    className="py-3 px-3.5 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs uppercase tracking-wider border-2 border-slate-600 rounded-lg active:translate-y-0.5 transition-all cursor-pointer"
                  >
                    Carte
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ========================================================================= */}
        {/* MODAL DU CLASSEMENT & HISTORIQUE DES TEMPS (LEADERBOARD SPEEDRUN)         */}
        {/* ========================================================================= */}
        {showLeaderboardModal && (
          <div className="absolute inset-0 z-[100] bg-black/90 backdrop-blur-md p-3 sm:p-5 md:p-6 flex flex-col justify-between animate-in fade-in zoom-in-95 duration-200">
            <div className="w-full max-w-2xl md:max-w-3xl mx-auto h-full flex flex-col justify-between">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-amber-500/40 pb-2 mb-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🏆</span>
                <div>
                  <h2 className="text-sm sm:text-base font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-yellow-200 to-cyan-300 uppercase tracking-wider">
                    Panthéon Céleste • Chronos
                  </h2>
                  <p className="text-[10px] text-slate-400 font-mono">
                    Ascension vers la Lune (50 Questions)
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowLeaderboardModal(false)}
                className="w-7 h-7 rounded-full bg-slate-800 text-slate-300 hover:text-white border border-slate-600 flex items-center justify-center text-xs font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Leaderboard Tabs */}
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() => setLeaderboardTab('GLOBAL')}
                className={`flex-1 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg border transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  leaderboardTab === 'GLOBAL'
                    ? 'bg-amber-500 text-slate-950 border-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.4)]'
                    : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'
                }`}
              >
                <span>🌍</span> <span>Top Chronos</span>
              </button>

              <button
                type="button"
                onClick={() => setLeaderboardTab('MY_HISTORY')}
                className={`flex-1 py-1.5 text-xs font-black uppercase tracking-wider rounded-lg border transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
                  leaderboardTab === 'MY_HISTORY'
                    ? 'bg-cyan-500 text-slate-950 border-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.4)]'
                    : 'bg-slate-900 text-slate-400 border-slate-700 hover:bg-slate-800'
                }`}
              >
                <span>📜</span> <span>Mes Tentatives ({playerHistory.length})</span>
              </button>
            </div>

            {/* Records List Table */}
            <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-1.5">
              {leaderboardTab === 'GLOBAL' ? (
                globalLeaderboard.length === 0 ? (
                  <div className="text-center text-slate-500 text-xs py-8">
                    Aucun chrono enregistré pour le moment.
                  </div>
                ) : (
                  globalLeaderboard.map((rec, idx) => {
                    const isCurrentRun = runResult && runResult.record.id === rec.id;
                    const isPlayer = rec.playerName.toLowerCase() === playerName.toLowerCase();

                    let medal = `#${idx + 1}`;
                    if (idx === 0) medal = '🥇 1er';
                    else if (idx === 1) medal = '🥈 2e';
                    else if (idx === 2) medal = '🥉 3e';

                    return (
                      <div
                        key={rec.id || idx}
                        className={`p-2 rounded-lg border flex items-center justify-between text-xs transition-all ${
                          isCurrentRun
                            ? 'bg-amber-950/80 border-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.5)]'
                            : isPlayer
                            ? 'bg-cyan-950/40 border-cyan-500/50'
                            : 'bg-slate-900/80 border-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`font-mono font-bold text-xs w-12 shrink-0 ${
                            idx === 0 ? 'text-amber-300' : idx === 1 ? 'text-slate-300' : idx === 2 ? 'text-amber-600' : 'text-slate-500'
                          }`}>
                            {medal}
                          </span>

                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className={`font-bold truncate ${isPlayer ? 'text-cyan-300' : 'text-slate-200'}`}>
                                {rec.playerName}
                              </span>
                              {isCurrentRun && (
                                <span className="text-[8px] bg-amber-400 text-slate-950 px-1 rounded font-black uppercase">
                                  Ce Run
                                </span>
                              )}
                            </div>
                            <span className="text-[9px] text-slate-500 font-mono block">
                              {rec.dateStr}
                            </span>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className="font-mono font-black text-sm text-cyan-300 drop-shadow-sm">
                            {rec.formattedTime}
                          </span>
                          <div className="text-[9px] text-slate-400 font-mono">
                            {rec.starsReached}/50 ⭐
                          </div>
                        </div>
                      </div>
                    );
                  })
                )
              ) : (
                playerHistory.length === 0 ? (
                  <div className="text-center text-slate-500 text-xs py-8">
                    Aucune tentative enregistrée pour {playerName}. Complétez l'ascension pour voir votre historique !
                  </div>
                ) : (
                  playerHistory.map((rec, idx) => {
                    const isCurrentRun = runResult && runResult.record.id === rec.id;
                    const prevRec = playerHistory[idx + 1];
                    const diffWithPrev = prevRec ? rec.timeSeconds - prevRec.timeSeconds : null;

                    return (
                      <div
                        key={rec.id || idx}
                        className={`p-2 rounded-lg border flex items-center justify-between text-xs transition-all ${
                          isCurrentRun
                            ? 'bg-amber-950/80 border-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.5)]'
                            : 'bg-slate-900/80 border-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] font-mono text-slate-400 font-bold shrink-0">
                            #{playerHistory.length - idx}
                          </span>

                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-200">
                                {rec.completed ? '🌕 Sommet Atteint' : '🌑 Éclipse'}
                              </span>
                              {isCurrentRun && (
                                <span className="text-[8px] bg-amber-400 text-slate-950 px-1 rounded font-black uppercase">
                                  Dernier
                                </span>
                              )}
                            </div>
                            <span className="text-[9px] text-slate-500 font-mono block">
                              {rec.dateStr} • {rec.starsReached}/50 étoiles
                            </span>
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className="font-mono font-black text-sm text-cyan-300">
                            {rec.formattedTime}
                          </span>
                          {diffWithPrev !== null && rec.completed && prevRec.completed && (
                            <div className={`text-[9px] font-mono font-bold ${diffWithPrev < 0 ? 'text-emerald-400' : 'text-amber-400'}`}>
                              {diffWithPrev < 0 ? `-${Math.abs(diffWithPrev).toFixed(1)}s 🚀` : `+${diffWithPrev.toFixed(1)}s`}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )
              )}
            </div>

            {/* Modal Bottom Buttons */}
            <div className="pt-2 border-t border-slate-800 flex gap-2 mt-1">
              <button
                type="button"
                onClick={() => setShowLeaderboardModal(false)}
                className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs uppercase tracking-wider rounded-lg border border-slate-600 cursor-pointer"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

        {/* ========================================================================= */}
        {/* BARRE D'ADMIN / TRICHE (CELESTIAL LEVEL)                                  */}
        {/* ========================================================================= */}
        {playerName === 'Admin' && (
          <div className="w-full bg-slate-950/95 border-t border-amber-500/70 px-2 py-1 pb-[max(env(safe-area-inset-bottom),6px)] flex items-center justify-between sm:justify-center gap-1 sm:gap-2 shrink-0 z-40 text-amber-300 shadow-[0_-2px_12px_rgba(0,0,0,0.6)] backdrop-blur-sm select-none min-h-[40px]">
            {/* Step navigation */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (currentQIndex > 0) {
                    setCurrentQIndex(prev => prev - 1);
                    setFeedback('IDLE');
                    setSelectedOption(null);
                    setIsGameOver(false);
                    setIsVictory(false);
                  }
                }}
                disabled={currentQIndex <= 0}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                  currentQIndex <= 0
                    ? 'text-slate-600 opacity-40 cursor-not-allowed'
                    : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
                }`}
                title="Étoile précédente"
              >
                <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="17,4 7,12 17,20" />
                </svg>
              </button>
              <span className="text-[10px] sm:text-xs text-amber-300 font-black uppercase tracking-wider whitespace-nowrap px-0.5">
                <span className="hidden sm:inline">👑 Étoile </span>
                <span className="sm:hidden">👑 </span>
                {currentQIndex + 1}/50
              </span>
              <button
                type="button"
                onClick={() => {
                  if (currentQIndex < TOTAL_QUESTIONS - 1) {
                    setCurrentQIndex(prev => prev + 1);
                    setFeedback('IDLE');
                    setSelectedOption(null);
                    setIsGameOver(false);
                    setIsVictory(false);
                  } else if (currentQIndex === TOTAL_QUESTIONS - 1) {
                    const finalTime = (Date.now() - startTime) / 1000;
                    triggerVictory(finalTime, lives, maxCombo);
                  }
                }}
                disabled={isVictory}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                  isVictory
                    ? 'text-slate-600 opacity-40 cursor-not-allowed'
                    : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
                }`}
                title="Étoile suivante"
              >
                <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="7,4 17,12 7,20" />
                </svg>
              </button>
            </div>

            {/* Level Switcher (N1 à N6 Céleste) - Fixed row, non scrollable */}
            {onAdminSwitchLevel && (
              <div className="flex items-center gap-0.5 sm:gap-1 border-l border-amber-500/40 pl-1.5 sm:pl-2 shrink-0">
                {[0, 1, 2, 3, 4, 5].map((lvlIdx) => {
                  const isCelestial = lvlIdx === 5;

                  return (
                    <button
                      type="button"
                      key={lvlIdx}
                      onClick={() => onAdminSwitchLevel(lvlIdx)}
                      className={`text-[10px] sm:text-xs font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border transition-all cursor-pointer flex items-center justify-center ${
                        isCelestial
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-indigo-200 shadow-[0_0_8px_rgba(99,102,241,0.9)] scale-105'
                          : 'bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-amber-300'
                      }`}
                      title={isCelestial ? 'Niveau 6 : Niveau Céleste (Actuel)' : `Changer pour Niveau ${lvlIdx + 1}`}
                    >
                      {`N${lvlIdx + 1}`}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
